import { EFinancingProgram, EPaymentFrequency, EProvince, PlanPromoRule } from '@if/core/types'
import { ETermMax } from '@src/data/types/constants'
import { NormCreditDecision } from '@src/data/types/CreditDecisionSchema'
import { MerchantPaymentPlan } from '@src/data/types/MerchantPaymentPlan'
import { createContext, useContext } from 'react'

export interface BaseWorksheetFormConfig {
  // Identity
  financingProgramId: EFinancingProgram
  creditApplicationId: string
  merchantId: string

  // Loan amount bounds (merged from FinancingConfig + program config)
  minLoanAmount: number
  maxLoanAmount: number

  // Loan term bounds (from FinancingConfig)
  minLoanTerm: number
  maxLoanTerm: number
  largeLoanAmount: number
  longTerm: number

  // APR limit (from FinancingConfig)
  effectiveRateThreshold: number

  // Program config
  supportedTerms: ETermMax[]
  supportedPaymentFrequencies: EPaymentFrequency[]
  supportsInsurance: boolean
  useAgentLeeway: boolean
  validateMaxPayment: boolean
  checkAvailableCredit: boolean
  checkActivationWithin90Days: boolean

  // Decision context
  normsCreditDecision: NormCreditDecision | null
  maxAmountFinanced?: number | null
  maxTermDuration?: number | null
  canAcceptHighEstimate?: boolean

  // Applicant / loan context
  hasCoApplicant: boolean
  stateIso: EProvince
  interestRate: number
  maxPmtAmount: number

  // Plans
  plans: MerchantPaymentPlan[]
  // Merchant promo plans currently usable (date-valid + promo-code-valid).
  // RegularDailyInterests is always selectable independently of this list.
  availablePaymentPlans: MerchantPaymentPlan[]
  planPromoRules?: PlanPromoRule[]
  createdOn: Date

  // Status / mode
  expiresOn: Date | string
  editLocked?: boolean
  isSinglePayment?: boolean
}

const BaseWorksheetFormConfigContext = createContext<BaseWorksheetFormConfig | null>(null)

export const BaseWorksheetFormConfigProvider = BaseWorksheetFormConfigContext.Provider

export const useBaseWorksheetFormConfig = (): BaseWorksheetFormConfig => {
  const value = useContext(BaseWorksheetFormConfigContext)
  if (!value) {
    throw new Error('useBaseWorksheetFormConfig must be used inside <BaseWorksheetForm config={...}>')
  }
  return value
}

const REQUIRED_FINANCING_CONFIG_KEYS = [
  'effectiveRateThreshold',
  'minLoanAmount',
  'maxLoanAmount',
  'minLoanTerm',
  'maxLoanTerm',
  'largeLoanAmount',
  'longTerm',
] as const satisfies readonly (keyof BaseWorksheetFormConfig)[]

export type RequiredFinancingConfigKey = (typeof REQUIRED_FINANCING_CONFIG_KEYS)[number]

export const findMissingFinancingConfigKeys = (
  config: Pick<BaseWorksheetFormConfig, RequiredFinancingConfigKey>,
): RequiredFinancingConfigKey[] => REQUIRED_FINANCING_CONFIG_KEYS.filter((key) => !config[key])
